import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import {
  CreateEmployerHealthInsuranceReduction,
  DeleteEmployerHealthInsuranceReduction,
  EmployerHealthInsuranceReduction,
  EmployerHealthInsuranceReductionEventPayload,
  EmployerHealthInsuranceReductionParams,
  EmployerHealthInsuranceReductionsResponse,
  UpdateEmployerHealthInsuranceReduction,
} from "./types";
import { RootState } from "../../../app/store";
import {
  createEmployerHealthInsuranceReduction,
  getEmployerHealthInsuranceReductions,
  removeEmployerHealthInsuranceReduction,
  saveEmployerHealthInsuranceReduction,
} from "./employerHealthInsuranceReductionsAPI";
import { EventPayload } from "../../../common/types/Event";
import { Hits } from "../../../common/types/Paginated";

export type SliceState = {
  ids: [];
  entities: {};
  cursor: string | null;
  hits?: Hits;
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

const employerHealthInsuranceReductionAdapter =
  createEntityAdapter<EmployerHealthInsuranceReduction>();

export const fetchEmployerHealthInsuranceReductions = createAsyncThunk<
  EmployerHealthInsuranceReductionsResponse,
  EmployerHealthInsuranceReductionParams,
  { state: RootState }
>(
  "employerHealthInsuranceReductions/fetchEmployerHealthInsuranceReductions",
  getEmployerHealthInsuranceReductions,
);

export const addEmployerHealthInsuranceReduction = createAsyncThunk<
  EventPayload<EmployerHealthInsuranceReductionEventPayload>[],
  CreateEmployerHealthInsuranceReduction,
  { state: RootState }
>(
  "employerHealthInsuranceReductions/addEmployerHealthInsuranceReduction",
  createEmployerHealthInsuranceReduction,
);

export const updateEmployerHealthInsuranceReduction = createAsyncThunk<
  EventPayload<EmployerHealthInsuranceReductionEventPayload>[],
  UpdateEmployerHealthInsuranceReduction,
  { state: RootState }
>(
  "employerHealthInsuranceReductions/updateEmployerHealthInsuranceReduction",
  saveEmployerHealthInsuranceReduction,
);

export const deleteEmployerHealthInsuranceReduction = createAsyncThunk<
  EventPayload<{}>[],
  DeleteEmployerHealthInsuranceReduction,
  { state: RootState }
>(
  "employerHealthInsuranceReductions/deleteEmployerHealthInsuranceReduction",
  removeEmployerHealthInsuranceReduction,
);

const employerHealthInsuranceReductionsSlice = createSlice({
  name: "employerHealthInsuranceReductionsSlice",
  initialState: employerHealthInsuranceReductionAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployerHealthInsuranceReductions.pending, (state) => {
        state.status = "pending";
      })
      .addCase(
        fetchEmployerHealthInsuranceReductions.fulfilled,
        (state, action) => {
          const { data, hits, cursor } = action.payload;
          employerHealthInsuranceReductionAdapter.setAll(state, data);
          state.cursor = cursor;
          state.hits = hits;
          state.status = "succeeded";
        },
      )
      .addCase(
        fetchEmployerHealthInsuranceReductions.rejected,
        (state, action) => {
          state.status = "failed";
          state.error = action.error.message;
        },
      )
      .addCase(addEmployerHealthInsuranceReduction.pending, (state) => {
        state.status = "pending";
      })
      .addCase(
        addEmployerHealthInsuranceReduction.fulfilled,
        (state, action) => {
          const data = action.payload[0]
            .payload as EmployerHealthInsuranceReduction;
          employerHealthInsuranceReductionAdapter.addOne(state, {
            ...data,
            id: action.payload[0].id,
            version: action.payload[0].version,
            employerName: action.meta.arg.employerName,
            employerCostCenterName: action.meta.arg.employerCostCenterName,
            customerOrganizationName: action.meta.arg.customerOrganizationName,
          });
          state.hits!.value += 1;
          state.status = "succeeded";
        },
      )
      .addCase(
        addEmployerHealthInsuranceReduction.rejected,
        (state, action) => {
          state.status = "failed";
          state.error = action.error.message;
        },
      )
      .addCase(updateEmployerHealthInsuranceReduction.pending, (state) => {
        state.status = "pending";
      })
      .addCase(
        updateEmployerHealthInsuranceReduction.fulfilled,
        (state, action) => {
          const { payload, version, id } = action.payload[0];
          employerHealthInsuranceReductionAdapter.updateOne(state, {
            id,
            changes: {
              ...payload,
              employerName: action.meta.arg.employerName,
              employerCostCenterName: action.meta.arg.employerCostCenterName,
              customerOrganizationName:
                action.meta.arg.customerOrganizationName || "",
              version,
            },
          });
          state.status = "succeeded";
        },
      )
      .addCase(
        updateEmployerHealthInsuranceReduction.rejected,
        (state, action) => {
          state.status = "failed";
          state.error = action.error.message;
        },
      )
      .addCase(deleteEmployerHealthInsuranceReduction.pending, (state) => {
        state.status = "pending";
      })
      .addCase(
        deleteEmployerHealthInsuranceReduction.fulfilled,
        (state, action) => {
          const { id } = action.meta.arg;
          employerHealthInsuranceReductionAdapter.removeOne(state, id);
          state.hits!.value -= 1;
          state.status = "succeeded";
        },
      )
      .addCase(
        deleteEmployerHealthInsuranceReduction.rejected,
        (state, action) => {
          state.status = "failed";
          state.error = action.error.message;
        },
      );
  },
});

export const {
  selectAll: selectAllEmployerHealthInsuranceReductions,
  selectById: selectEmployerHealthInsuranceReductionById,
} = employerHealthInsuranceReductionAdapter.getSelectors(
  (state: RootState) => state.employerHealthInsuranceReductions,
);

export const employerHealthInsuranceReductionsReducer =
  employerHealthInsuranceReductionsSlice.reducer;
